import { zodResolver } from '@hookform/resolvers/zod';
import { Group, Stack } from '@mantine/core';
import { ReactNode } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Content, Form, FormSubmit, NumberInputControl, SwitchControl } from '@/core/components';

import { LeadTimeEnforcementSelectControl } from './components/form/LeadTimeEnforcementSelectControl';
import { LeadTimeUnitSelectControl } from './components/form/LeadTimeUnitSelectControl';
import { TravelEnforcementSelectControl } from './components/form/TravelEnforcementSelectControl';
import { TravelReturnModeSelectControl } from './components/form/TravelReturnModeSelectControl';
import { TravelSettingsSchema } from './model/TravelSettingsSchema';

interface TravelSettingsFormProps extends FormSubmit<TravelSettingsSchema> {
  title: string;
  // Rendered last so it sits beside the save button that raised it.
  notice?: ReactNode;
}

export function TravelSettingsForm(props: TravelSettingsFormProps) {
  const { title, notice, schema, defaultValues, values, isSubmitting, onCancel, onSubmit } = props;
  const { t } = useTranslation();

  const form = useForm<TravelSettingsSchema>({
    defaultValues,
    values,
    resolver: zodResolver(schema),
  });

  return (
    <FormProvider {...form}>
      <Form
        title={title}
        isSubmitting={isSubmitting}
        onCancel={onCancel}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <Stack>
          <Content
            type="full"
            title={t('travel.settings.sections.leadTime')}
          >
            <Group grow>
              <NumberInputControl
                name="daysBeforeDeparture"
                label={t('travel.settings.fields.daysBeforeDeparture')}
                min={0}
                allowDecimal={false}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
              <LeadTimeUnitSelectControl
                name="leadTimeUnit"
                label={t('travel.settings.fields.leadTimeUnit')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
            </Group>
            <Group grow>
              <NumberInputControl
                name="erasmusDaysBeforeDeparture"
                label={t('travel.settings.fields.erasmusDaysBeforeDeparture')}
                min={0}
                allowDecimal={false}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
              <LeadTimeUnitSelectControl
                name="erasmusLeadTimeUnit"
                label={t('travel.settings.fields.erasmusLeadTimeUnit')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
            </Group>
            <LeadTimeEnforcementSelectControl
              name="leadTimeEnforcement"
              label={t('travel.settings.fields.leadTimeEnforcement')}
              isSubmitting={isSubmitting}
              withAsterisk
              control={form.control}
            />
          </Content>
          <Content
            type="full"
            title={t('travel.settings.sections.parallel')}
          >
            <Group grow>
              <TravelEnforcementSelectControl
                name="parallelEnforcement"
                label={t('travel.settings.fields.parallelEnforcement')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
              <TravelEnforcementSelectControl
                name="erasmusParallelEnforcement"
                label={t('travel.settings.fields.erasmusParallelEnforcement')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
            </Group>
          </Content>
          <Content
            type="full"
            title={t('travel.settings.sections.dayBalance')}
          >
            <Group grow>
              <TravelEnforcementSelectControl
                name="leaveDaysEnforcement"
                label={t('travel.settings.fields.leaveDaysEnforcement')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
              <TravelEnforcementSelectControl
                name="dayCapEnforcement"
                label={t('travel.settings.fields.dayCapEnforcement')}
                isSubmitting={isSubmitting}
                withAsterisk
                control={form.control}
              />
            </Group>
          </Content>
          <Content
            type="full"
            title={t('travel.settings.sections.approval')}
          >
            <TravelReturnModeSelectControl
              name="returnMode"
              label={t('travel.settings.fields.returnMode')}
              isSubmitting={isSubmitting}
              withAsterisk
              control={form.control}
            />
            <Group
              grow
              align="flex-start"
            >
              <SwitchControl
                name="researchSecretariatVerification"
                label={t('travel.settings.fields.researchSecretariatVerification')}
                description={t('travel.settings.hints.secretariatVerification')}
                isSubmitting={isSubmitting}
                control={form.control}
              />
              <SwitchControl
                name="internationalSecretariatVerification"
                label={t('travel.settings.fields.internationalSecretariatVerification')}
                description={t('travel.settings.hints.secretariatVerification')}
                isSubmitting={isSubmitting}
                control={form.control}
              />
            </Group>
            <NumberInputControl
              name="reminderIntervalDays"
              label={t('travel.settings.fields.reminderIntervalDays')}
              description={t('travel.settings.hints.reminderIntervalDays')}
              min={1}
              allowDecimal={false}
              isSubmitting={isSubmitting}
              withAsterisk
              control={form.control}
            />
          </Content>
          {notice}
        </Stack>
      </Form>
    </FormProvider>
  );
}
